import { default as imgEmptyNoData } from 'assets/images/img_emptyview_no_data.png';
import KTBSButton, {
  KTBSButtonColor,
  KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import EmptyView from 'general/components/Others/EmptyView';
import VITableRowDropDown from 'general/components/VITableRowDropDown';
import AppColors from 'general/constants/AppColors';
import Utils from 'general/utils/Utils';
import _ from 'lodash';

const UIHelper = {
  buildCardItemInfo: (
    name,
    value,
    valueColor = AppColors.gray[800],
    showBorderBottom = true,
    borderBottomDashed = false,
    isEdit = false,
    handleEdit = () => { }
  ) => {
    return (
      <div
        className="d-flex flex-row justify-content-between align-items-center py-4"
        style={{
          borderBottom: showBorderBottom
            ? borderBottomDashed
              ? `1px dashed ${AppColors.gray[300]}`
              : `1px solid ${AppColors.gray[300]}`
            : '',
        }}
      >
        <span
          style={{
            fontWeight: '500',
            color: AppColors.gray[600],
          }}
        >
          {name}
        </span>
        <span
          style={{
            fontWeight: '600',
            color: valueColor,
          }}
        >
          {value}

          {isEdit && (
            <i
              className="fa-regular fa-pen-to-square ml-3 cursor-pointer"
              style={{ color: '#4A5677' }}
              onClick={handleEdit}
            />
          )}
        </span>
      </div>
    );
  },

  // MARK: ---- CARDS ----
  buildCard: (
    children,
    overflowHidden = true,
    removeStyle = false,
    shadow = true,
    fullHeight = false,
    borderColor = AppColors.gray[300],
    removeBorderTop = false
  ) => {
    return !removeStyle ? (
      <div
        className={`bg-white ${shadow ? 'shadow-sm' : ''} ${overflowHidden ? 'overflow-hidden' : ''
          } ${fullHeight ? 'h-100' : ''}`}
        style={{
          borderTopLeftRadius: removeBorderTop ? '0' : '10px',
          borderTopRightRadius: '10px',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          borderTop: removeBorderTop ? 'none' : `1px solid ${borderColor}`,
          borderBottom: `1px solid ${borderColor}`,
          borderLeft: `1px solid ${borderColor}`,
          borderRight: `1px solid ${borderColor}`,
        }}
      >
        {children}
      </div>
    ) : (
      <div>{children}</div>
    );
  },
  buildCardInfo: (children, overflowHidden = true) => {
    return (
      <div
        className={`bg-white ${overflowHidden ? 'overflow-hidden' : ''}`}
        style={{
          borderRadius: '5px',
          border: `1px solid ${AppColors.gray[300]}`,
        }}
      >
        {children}
      </div>
    );
  },
  buildCardHeader: (
    title,
    showDivider = false,
    actionElement,
    isLarge = false,
    isExtraLarge = false,
    subTitle = ''
  ) => {
    return (
      <div
        className="d-flex align-items-center justify-content-between px-4 py-3"
        style={{
          borderBottom: showDivider ? `1px solid ${AppColors.gray[300]}` : '',
        }}
      >
        <div className="d-flex flex-column">
          <span
            style={UIHelper.buildFontStyle(
              '600',
              isExtraLarge ? '18px' : isLarge ? '16px' : '14px',
              AppColors.gray[900]
            )}
          >
            {title}
          </span>
          {subTitle && subTitle}
        </div>
        {actionElement && actionElement}
      </div>
    );
  },
  buildCardCellItem: (name, value, onPressed = null, borderBottom = true, valueColor = AppColors.gray[800]) => {
    return (
      <div
        className="d-flex align-items-center justify-content-between px-3 py-3"
        style={{
          borderBottom: borderBottom ? `1px dashed ${AppColors.gray[300]}` : '',
        }}
      >
        <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[600])}>{name}</span>

        {_.isString(value) ? (
          <span
            className={`text-ellipsis-2-lines ${onPressed ? 'cursor-pointer hover-opacity-60' : ''
              } text-right`}
            onClick={onPressed ? onPressed : () => { }}
            style={{
              maxWidth: '60%',
              // overflow: 'hidden',
              // whiteSpace: 'nowrap',
              // textOverflow: 'ellipsis',
              ...UIHelper.buildFontStyle(
                '600',
                '13px',
                onPressed ? AppColors.primary : valueColor ? valueColor : AppColors.gray[800]
              ),
            }}
          >
            {value}
          </span>
        ) : (
          value
        )}
      </div>
    );
  },

  // MARK: ---- BUTTONS ----
  buildWhiteButton: (
    title,
    onPress,
    iconElement,
    fill = false,
    isLoading = false,
    borderRadius = 5
  ) => {
    return (
      <KTBSButton
        color={KTBSButtonColor.light}
        title={title}
        size={KTBSButtonSize.default}
        borderRadius={borderRadius}
        buttonTitleFontWeight="600"
        buttonTitleColor={AppColors.gray[800]}
        buttonTitleFontSize={14}
        disabled={_.isNull(onPress) || isLoading}
        onPress={onPress}
        iconElement={!isLoading ? iconElement : <div className="mr-8 spinner spinner-white"></div>}
        iconOnly={iconElement && _.isEmpty(title)}
        additionalClassName={`${fill ? 'flex-fill' : ''} bg-white border bg-hover-light`}
      />
    );
  },
  buildNegativeButton: (title, onPress, iconElement, fill = false, isLoading = false) => {
    return (
      <KTBSButton
        color={KTBSButtonColor.light}
        title={title}
        size={KTBSButtonSize.default}
        borderRadius={5}
        buttonTitleFontWeight="600"
        buttonTitleColor={AppColors.gray[800]}
        buttonTitleFontSize={14}
        disabled={_.isNull(onPress) || isLoading}
        onPress={onPress}
        iconElement={!isLoading ? iconElement : <div className="mr-8 spinner spinner-white"></div>}
        iconOnly={iconElement && _.isEmpty(title)}
        additionalClassName={fill ? 'flex-fill' : ''}
      />
    );
  },
  buildGreenButton: (title, onPress, iconElement, fill = false, isLoading = false) => {
    return (
      <KTBSButton
        color={KTBSButtonColor.success}
        title={title}
        size={KTBSButtonSize.default}
        borderRadius={5}
        buttonTitleFontWeight="600"
        buttonTitleColor="#fff"
        buttonTitleFontSize={13}
        onPress={onPress}
        iconElement={!isLoading ? iconElement : <div className="mr-8 spinner spinner-white"></div>}
        additionalClassName={fill ? 'flex-fill' : ''}
        disabled={_.isNull(onPress) || isLoading}
      />
    );
  },
  buildBlueButton: (title, onPress, iconElement, fill = false, isLoading = false) => {
    return (
      <KTBSButton
        color={KTBSButtonColor.primary}
        title={title}
        size={KTBSButtonSize.default}
        borderRadius={5}
        buttonTitleFontWeight="600"
        buttonTitleColor="#fff"
        buttonTitleFontSize={13}
        onPress={onPress}
        iconElement={!isLoading ? iconElement : <div className="mr-8 spinner spinner-white"></div>}
        additionalClassName={fill ? 'flex-fill' : ''}
        disabled={isLoading}
      />
    );
  },
  buildRedButton: (title, onPress, iconElement, fill = false, isLoading = false) => {
    return (
      <KTBSButton
        color={KTBSButtonColor.danger}
        title={title}
        size={KTBSButtonSize.default}
        borderRadius={5}
        buttonTitleFontWeight="600"
        buttonTitleColor="#fff"
        buttonTitleFontSize={13}
        onPress={onPress}
        iconElement={!isLoading ? iconElement : <div className="mr-8 spinner spinner-white"></div>}
        additionalClassName={fill ? 'flex-fill' : ''}
        disabled={isLoading}
      />
    );
  },

  // MARK: ---- STYLES ----
  buildFontStyle: (fontWeight, fontSize, color) => {
    return {
      fontWeight: fontWeight,
      fontSize: fontSize,
      color: color,
    };
  },

  // MARK: ---- FORMS ----
  buildFormLabel: (label, required = false, fullWidth = true, marginClass = 'mb-1') => {
    return (
      <label
        className={`${marginClass} text-left ${fullWidth ? 'w-100' : ''}`}
        style={UIHelper.buildFontStyle('700', '13px', AppColors.gray[800])}
      >
        {label}&nbsp;
        {required && <span style={{ color: AppColors.danger }}>*</span>}
      </label>
    );
  },

  buildFormText: (text) => {
    return (
      <label className="mt-1" style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[800])}>
        {text}
      </label>
    );
  },

  // MARK: ---- OTHERS ---
  buildBreadcrumb: (items) => {
    return (
      <nav aria-label="breadcrumb">
        <ol
          className="breadcrumb bg-white px-5"
          style={{
            borderBottom: `1px solid ${AppColors.gray[300]}`,
          }}
        >
          {items.map((item, index) => (
            <li
              key={index}
              className={`breadcrumb-item ${index === items.length - 1 ? 'active' : 'cursor-pointer'
                }`}
              aria-current={index === items.length - 1 ? 'page' : ''}
              style={
                (index !== items.length - 1 &&
                  UIHelper.buildFontStyle('700', '14px', AppColors.gray[800])) ||
                UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])
              }
              onClick={item?.onPress}
            >
              {item?.title}
            </li>
          ))}
        </ol>
      </nav>
    );
  },
  buildNameAndValue: (name, value) => {
    return (
      <div className="d-flex flex-column">
        <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[800])}>{value}</span>
        <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>{name}</span>
      </div>
    );
  },
  // build avatar
  buildAvatar: (url, size = 44, defaultAvatar, shadow = true, circle = true, borderRadius = 5) => {
    const imageUrl = url || defaultAvatar;

    return (
      <div
        className={`d-flex align-items-center justify-content-center ${shadow ? 'shadow' : ''}`}
        style={{
          border: `1px solid ${AppColors.gray[300]}`,
          width: size,
          height: size,
          borderRadius: circle ? '50%' : `${borderRadius}px`,
          overflow: 'hidden',
        }}
      >
        <img
          src={imageUrl}
          alt="avatar"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </div>
    );
  },

  // build section title
  buildSectionTitle: (title) => {
    return (
      <div className="d-flex align-items-center gap-2">
        <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>{title}</span>
      </div>
    );
  },
  buildSectionCard: (children) => {
    return (
      <div
        className=""
        style={{
          border: `1px solid ${AppColors.gray[300]}`,
          borderRadius: '5px',
        }}
      >
        {children}
      </div>
    );
  },
  // build more actions button
  buildMoreActionsButton: (actions, inTableCell = false, keepOriginal = false) => {
    if (!actions || actions.length === 0) {
      return <></>;
    }

    if (!inTableCell) {
      return (
        <VITableRowDropDown
          keepOriginal={keepOriginal}
          toggleElement={
            <a
              className={
                !inTableCell
                  ? 'btn btn-sm btn-clean btn-icon h-45px w-45px btn-light'
                  : 'btn btn-sm btn-clean btn-icon p-0'
              }
            >
              <i
                className="fa-solid fa-ellipsis"
                style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
              />
            </a>
          }
          menuItem={actions}
          singleBtnBgColor={AppColors.gray[200]}
        />
      );
    }

    const pickActions = _.slice(actions, 0, 2);
    const remainingActions = _.slice(actions, 2);

    return (
      <div
        className={`d-flex align-items-center gap-2${
          actions.length === 1 ? ' justify-content-center w-100' : ''
        }`}
      >
        {pickActions.map((action, index) => {
          return (
            <KTTooltip key={index} offset={[0, 0]} text={action.text}>
              <a
                className="btn btn-sm btn-clean btn-icon btn-table-action"
                onClick={() => {
                  action.click();
                }}
              >
                {action.iconElement}
              </a>
            </KTTooltip>
          );
        })}
        {remainingActions.length > 0 && (
          <VITableRowDropDown
            toggleElement={
              <a
                className={
                  !inTableCell
                    ? 'btn btn-sm btn-clean btn-icon h-45px w-45px btn-light'
                    : 'btn btn-sm btn-clean btn-icon p-0'
                }
              >
                <i
                  className="fa-regular fa-ellipsis"
                  style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                />
              </a>
            }
            menuItem={remainingActions}
          />
        )}
      </div>
    );
  },

  buildEmptyNoData: (text) => {
    return (
      <EmptyView
        icon={imgEmptyNoData}
        title="Không có dữ liệu để hiển thị"
        text={<span>{text}</span>}
      />
    );
  },

  buildAlert: (iconEl, title, text, color, bgColor) => {
    return (
      <div
        className="p-4 d-flex align-items-start gap-3"
        style={{
          border: `1px dashed ${color}`,
          backgroundColor: bgColor,
        }}
      >
        <div className="pt-2">{iconEl}</div>
        <div className="d-flex flex-column">
          <span style={UIHelper.buildFontStyle('700', '16px', color)}>{title}</span>
          <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>{text}</span>
        </div>
      </div>
    );
  },

  buildTableColumn: (title, unit = '') => {
    if (_.isEmpty(unit)) {
      return (
        <span
          className="letter-spacing-unset"
          style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
        >
          {title}
        </span>
      );
    } else {
      return (
        <div className="d-flex flex-column align-items-end">
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            {title}
          </span>
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
          >
            {unit}
          </span>
        </div>
      );
    }
  },

  buildDoughnutChartLegend: (items) => {
    return (
      <div className="d-flex flex-column">
        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;
          return (
            <div
              key={index}
              className="d-flex align-items-center gap-2 py-2"
              style={{
                borderBottom: isLastItem ? 'none' : `1px dashed ${AppColors.gray[200]}`,
              }}
            >
              {Utils.isValid(item?.color) && (
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '4px',
                    backgroundColor: item?.color,
                  }}
                ></div>
              )}

              <div className="d-flex align-items-center justify-content-between w-100">
                <span
                  className="letter-spacing-unset"
                  style={UIHelper.buildFontStyle('400', '12px', 'rgba(126, 130, 153, 1)')}
                >
                  {item?.title}
                </span>
                <span
                  className="letter-spacing-unset"
                  style={UIHelper.buildFontStyle('600', '12px', 'rgba(73, 73, 104, 1)')}
                >
                  {item?.value}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  },

  buildCellSystemInfo: (iconEl, title, time, unread = true, onPress, showDivider = true) => {
    return (
      <div
        className={`px-4 py-2 d-flex align-items-center justify-content-between gap-2 ${onPress ? 'hover-opacity-60' : ''
          }`}
        style={{
          borderBottom: showDivider ? `1px solid ${AppColors.gray[200]}` : '',
          cursor: onPress ? 'pointer' : 'default',
        }}
        onClick={() => onPress()}
      >
        {iconEl && iconEl}
        <div className="d-flex flex-column flex-fill">
          <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>{title}</span>
          <span style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[600])}>{time}</span>
        </div>
        {unread && (
          <div
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: AppColors.primary,
              flexShrink: 0,
            }}
          ></div>
        )}
      </div>
    );
  },

  buildToggleSwitch: (isOn, handleToggle) => {
    return (
      <div
        className="position-relative d-inline-block"
        style={{
          width: 43,
          height: 26,
        }}
        onClick={() => handleToggle()}
      >
        <div
          className="rounded-pill"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: isOn ? AppColors.primary : AppColors.gray[400],
            transition: 'background-color 0.3s ease',
            cursor: 'pointer',
          }}
        />
        <div
          className="position-absolute rounded-circle"
          style={{
            width: 22,
            height: 22,
            backgroundColor: 'white',
            top: 2,
            left: isOn ? 19 : 2,
            transition: 'left 0.3s ease',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
          }}
        />
      </div>
    );
  },

  // MARK: ---- DASHBOARD ----
  buildStatisticItem: (color, name, value, icon, faIcon) => {
    return (
      <div className="d-flex align-items-center gap-2">
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            aspectRatio: '1',
            height: '60px',
            borderRadius: '10px',
            backgroundColor: color,
          }}
        >
          {Utils.isValid(icon) ? (
            <img alt={name} src={icon} />
          ) : (
            <i
              className={`text-white ${faIcon}`}
              style={{
                fontSize: '2.5rem',
              }}
            />
          )}
        </div>
        <div className="d-flex flex-column">
          <span style={UIHelper.buildFontStyle('700', '20px', color)}>{value}</span>
          <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>{name}</span>
        </div>
      </div>
    );
  },
};

export default UIHelper;
